import {
  FolderTreeItem,
  useFolderActionPermissions,
  useSplitTreatments,
} from "@zapier/client";
import {
  KebabMenuItem,
  useEmitFolderActionEvent,
  FolderActionInteractionOption,
  FolderModal,
} from "@zapier/shared";
import { useMemo } from "react";
import {
  FolderTypeQueryState,
  FOLDER_TRASH_ID,
  isPersonalFolder,
} from "@zapier/utils";
import { useTranslations } from "@zapier/i18n";

interface Input {
  folder?: FolderTreeItem;
  folderLevel: number;
  setCurrentModal: (modal: FolderModal | undefined) => void;
  setCurrentFolder: (folder: FolderTreeItem | undefined) => void;
}

export function useFolderActionItems({
  folder,
  folderLevel,
  setCurrentFolder,
  setCurrentModal,
}: Input) {
  const { emit } = useEmitFolderActionEvent();
  const { isDuplicateAssetsEnabled } = useSplitTreatments();
  const tAction = useTranslations("buttons");
  const showCopyToAnotherAccountAction =
    isDuplicateAssetsEnabled && folder?.id !== FOLDER_TRASH_ID;
  const isCopyToAnotherAccountDisabled = isPersonalFolder(folder?.id);

  const {
    canMove,
    canRename,
    disabledDeleteMessage,
    disabledMoveMessage,
    disabledShareMessage,
    canCreateSubfolder,
    canCreateSubfolderLimit,
    subfolderLimitMessage,
    isLoading,
  } = useFolderActionPermissions({
    folderId: folder?.id,
    folderDepth: folderLevel + 1, // Pass child's depth to check if creation is allowed
  });

  const disabledCreateSubfolderMessage =
    canCreateSubfolder && !canCreateSubfolderLimit
      ? subfolderLimitMessage
      : undefined;
  return useMemo(() => {
    const items: (KebabMenuItem | undefined)[] = [
      {
        icon: "actionEdit",
        label: "Rename",
        onClick: () => {
          setCurrentFolder(folder);
          setCurrentModal(FolderModal.Rename);
        },
        disabled: isLoading || !canRename,
      },
      {
        icon: "personAdd",
        label: "Share folder",
        onClick: () => {
          setCurrentFolder(folder);
          setCurrentModal(FolderModal.Share);
        },
        disabled: isLoading || !!disabledShareMessage,
        tooltipText: disabledShareMessage,
      },
      {
        icon: "navFolder",
        label: "Create subfolder",
        disabled:
          isLoading ||
          !canCreateSubfolder ||
          (canCreateSubfolder && !canCreateSubfolderLimit),
        tooltipText: disabledCreateSubfolderMessage,
        onClick: () => {
          emit({
            optionSelected: FolderActionInteractionOption.CreateSubfolder,
            interactionRawText: folder?.isShared
              ? FolderTypeQueryState.Shared
              : FolderTypeQueryState.Personal,
          });
          setCurrentFolder(folder);
          setCurrentModal(FolderModal.Create);
        },
      },
      {
        icon: "actionMoveToFolder",
        label: tAction("move-to-folder"),
        onClick: () => {
          emit({
            optionSelected: FolderActionInteractionOption.MoveFolder,
          });
          setCurrentFolder(folder);
          setCurrentModal(FolderModal.Move);
        },
        disabled: isLoading || !canMove,
        tooltipText: disabledMoveMessage || undefined,
      },
      showCopyToAnotherAccountAction
        ? { divider: true, label: "divider" }
        : undefined,
      showCopyToAnotherAccountAction
        ? {
            icon: "arrowExit",
            label: tAction("copy-to-another-account"),
            disabled: isCopyToAnotherAccountDisabled,
            onClick: () => {
              setCurrentFolder(folder);
              setCurrentModal(FolderModal.CopyToAnotherAccount);
            },
          }
        : undefined,
      { divider: true, label: "divider" },
      {
        icon: "actionTrash",
        label: tAction("delete"),
        onClick: () => {
          setCurrentFolder(folder);
          setCurrentModal(FolderModal.Delete);
        },
        disabled: isLoading || Boolean(disabledDeleteMessage),
        hasWarning: true,
        tooltipText: disabledDeleteMessage,
      },
    ];

    return items.filter((item): item is KebabMenuItem => item !== undefined);
  }, [
    isLoading,
    canRename,
    disabledShareMessage,
    canCreateSubfolder,
    canCreateSubfolderLimit,
    disabledCreateSubfolderMessage,
    canMove,
    disabledMoveMessage,
    showCopyToAnotherAccountAction,
    isCopyToAnotherAccountDisabled,
    disabledDeleteMessage,
    folder,
    setCurrentFolder,
    setCurrentModal,
    emit,
    tAction,
  ]);
}
